import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { bind } from 'bind-event-listener';
import { Logger } from './logger';
let unbindFunctions = [];

/**
 * Modeled after IntersectionObserver and MutationObserver, this Image observer
 * reports image load times for images that are visible within the viewport.
 *
 * Note: ImageObserver treats iframes as images to give accurate times
 *
 * @example
 * const observer = new InViewportImageObserver((timestamp) => {
 *   // do something with the image load event time
 * });
 *
 * // begin listening for all visible image load events
 * // note that observe() takes no arguments
 * observer.observe();
 *
 * // stop listening to image load events
 * observer.disconnect();
 */
export class InViewportImageObserver {
  constructor(callback) {
    _defineProperty(this, "imageLoadTimes", new Map());
    _defineProperty(this, "lastImageLoadTimestamp", 0);
    _defineProperty(this, "intersectionObserverCallback", entries => {
      entries.forEach(entry => {
        const img = entry.target;
        const timestamp = this.imageLoadTimes.get(img);
        if (entry.isIntersecting && timestamp != null) {
          Logger.info('InViewportImageObserver.callback()', '::', 'timestamp =', timestamp);
          this.callback(timestamp, img);
        }
        this.intersectionObserver.unobserve(img);
        this.imageLoadTimes.delete(img);
      });
    });
    // Error events on the `window` can be a `string` (legacy).
    // The built in TS DOM types don't have a (correct) narrower
    // type for the `Document`.
    _defineProperty(this, "handleLoadOrErrorEvent", event => {
      if (typeof event !== 'string' && (event.target instanceof HTMLImageElement || event.target instanceof HTMLIFrameElement)) {
        Logger.debug('InViewportImageObserver.handleLoadOrErrorEvent()', '::', 'event =', event);
        this.imageLoadTimes.set(event.target, event.timeStamp);
        this.intersectionObserver.observe(event.target);
      }
    });
    this.callback = callback;
    this.intersectionObserver = new IntersectionObserver(this.intersectionObserverCallback);
  }
  /** Start observing loading images. */
  observe() {
    Logger.info('InViewportImageObserver.observe()');
    const loadUnbindFn = bind(document, {
      type: 'load',
      listener: this.handleLoadOrErrorEvent,
      options: {
        capture: true
      }
    });
    const errorUnbindFn = bind(document, {
      type: 'error',
      listener: this.handleLoadOrErrorEvent,
      options: {
        capture: true
      }
    });
    unbindFunctions.push(loadUnbindFn);
    unbindFunctions.push(errorUnbindFn);
  }

  /** Stop observing loading images, and clean up. */
  disconnect() {
    Logger.info('InViewportImageObserver.disconnect()');
    this.lastImageLoadTimestamp = 0;
    this.imageLoadTimes.clear();
    this.intersectionObserver.disconnect();
    unbindFunctions.forEach(unbind => {
      unbind();
    });
    unbindFunctions = [];
  }
}